import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import ReportCard from './components/ReportCard';

const mockUser = { id: 1, name: 'Sarah Mitchell', email: 'sarah@email.com', role: 'user' };

const mockReports = [
  { id: 1, address: '42 Harbour View Terrace', suburb: 'Mosman NSW 2088', date: '20 Feb 2026', verdict: 'fair', price: '$2,800,000', offerMin: '$2,590,000', confidence: 87, type: 'House', beds: 4 },
  { id: 2, address: '15 Ocean Street', suburb: 'Bondi Beach NSW 2026', date: '18 Feb 2026', verdict: 'overpriced', price: '$1,950,000', offerMin: '$1,720,000', confidence: 82, type: 'Apartment', beds: 3 },
  { id: 3, address: '7 Clifton Road', suburb: 'Mosman NSW 2088', date: '15 Feb 2026', verdict: 'underpriced', price: '$2,450,000', offerMin: '$2,450,000', confidence: 79, type: 'House', beds: 5 },
  { id: 4, address: '22 Bay View Avenue', suburb: 'Neutral Bay NSW 2089', date: '12 Feb 2026', verdict: 'fair', price: '$1,650,000', offerMin: '$1,520,000', confidence: 91, type: 'Townhouse', beds: 3 },
  { id: 5, address: '8 Cremorne Point Road', suburb: 'Cremorne Point NSW 2090', date: '10 Feb 2026', verdict: 'overpriced', price: '$3,200,000', offerMin: '$2,850,000', confidence: 76, type: 'House', beds: 4 },
  { id: 6, address: '33 Military Road', suburb: 'Mosman NSW 2088', date: '8 Feb 2026', verdict: 'fair', price: '$2,100,000', offerMin: '$1,950,000', confidence: 88, type: 'Apartment', beds: 2 },
];

const Reports = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState(mockReports);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  const filtered = reports?.filter(r => filter === 'all' || r?.verdict === filter)?.filter(r => !search?.trim() || r?.address?.toLowerCase()?.includes(search?.toLowerCase()) || r?.suburb?.toLowerCase()?.includes(search?.toLowerCase()))?.sort((a, b) => {
      if (sortBy === 'date') return b?.id - a?.id;
      if (sortBy === 'confidence') return b?.confidence - a?.confidence;
      if (sortBy === 'price') return parseInt(b?.price?.replace(/[^0-9]/g, '')) - parseInt(a?.price?.replace(/[^0-9]/g, ''));
      return 0;
    });

  const handleDelete = (id) => setReports(prev => prev?.filter(r => r?.id !== id));
  const handleView = (report) => navigate('/analysis-detail', { state: { address: report?.address } });
  const handleDownload = (report) => alert(`Downloading PDF for ${report?.address}`);
  const verdictCounts = { all: reports?.length, fair: reports?.filter(r => r?.verdict === 'fair')?.length, overpriced: reports?.filter(r => r?.verdict === 'overpriced')?.length, underpriced: reports?.filter(r => r?.verdict === 'underpriced')?.length };

  return (
    <div className="min-h-screen bg-background">
      <Header user={mockUser} onLogout={() => navigate('/authentication')} />
      <main className="pt-16">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-8 md:py-12">
          <div className="mb-8">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4 text-sm">
              <Icon name="ArrowLeft" size={16} color="currentColor" />
              Back to Home
            </button>
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(170,136,255,0.15)', border: '1px solid rgba(170,136,255,0.3)' }}>
                    <Icon name="FileText" size={20} color="#AA88FF" />
                  </div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground">Analysis Reports</h1>
                </div>
                <p className="text-muted-foreground">All your saved property analysis reports in one place.</p>
              </div>
              <button onClick={() => navigate('/dashboard')} className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium" style={{ background: 'linear-gradient(135deg, #00D4FF, #0099CC)', color: '#000' }}>
                <Icon name="Plus" size={16} color="#000" />
                New Analysis
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Reports', value: reports?.length, color: '#AA88FF', icon: 'FileText' },
              { label: 'Fair Value', value: verdictCounts?.fair, color: '#00FF88', icon: 'CheckCircle' },
              { label: 'Overpriced', value: verdictCounts?.overpriced, color: '#FF4444', icon: 'TrendingUp' },
              { label: 'Underpriced', value: verdictCounts?.underpriced, color: '#00D4FF', icon: 'TrendingDown' },
            ]?.map((s, i) => (
              <div key={i} className="bg-card rounded-xl border border-border p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name={s?.icon} size={16} color={s?.color} />
                  <span className="text-xs text-muted-foreground">{s?.label}</span>
                </div>
                <p className="text-2xl font-bold" style={{ color: s?.color }}>{s?.value}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Icon name="Search" size={18} color="#888" className="absolute left-4 top-1/2 -translate-y-1/2" />
              <input type="text" value={search} onChange={e => setSearch(e?.target?.value)} placeholder="Search by address or suburb..." className="w-full pl-11 pr-4 py-3 rounded-xl bg-card border border-border text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary transition-colors" />
            </div>
            <select value={filter} onChange={e => setFilter(e?.target?.value)} className="px-4 py-3 rounded-xl bg-card border border-border text-foreground focus:outline-none focus:border-primary">
              <option value="all">All Verdicts ({verdictCounts?.all})</option>
              <option value="fair">Fair Value ({verdictCounts?.fair})</option>
              <option value="overpriced">Overpriced ({verdictCounts?.overpriced})</option>
              <option value="underpriced">Underpriced ({verdictCounts?.underpriced})</option>
            </select>
            <select value={sortBy} onChange={e => setSortBy(e?.target?.value)} className="px-4 py-3 rounded-xl bg-card border border-border text-foreground focus:outline-none focus:border-primary">
              <option value="date">Sort: Newest</option>
              <option value="confidence">Sort: Confidence</option>
              <option value="price">Sort: Price</option>
            </select>
          </div>

          {filtered?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered?.map(report => (
                <ReportCard key={report?.id} report={report} onView={handleView} onDownload={handleDownload} onDelete={handleDelete} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Icon name="FileText" size={48} color="#444" />
              <p className="text-muted-foreground mt-3 text-lg">No reports found</p>
              <p className="text-sm text-muted-foreground mt-1">Try adjusting your search or filters.</p>
              <button onClick={() => navigate('/dashboard')} className="mt-4 px-6 py-2 rounded-xl text-sm font-medium" style={{ background: 'rgba(0,212,255,0.1)', color: '#00D4FF', border: '1px solid rgba(0,212,255,0.3)' }}>Run Your First Analysis</button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Reports;
